import React, { useCallback, useMemo } from "react";
import { FormConfiguration } from "../shared/crud/form";
import { LegalInfo } from "./model";
import { translationFieldConfig } from "../shared/widgets/translation";
import { usePropertyLocales } from "../../components/property-locale-provider";
import SimpleCrud, { CrudMessages, defaultCrudMessages } from "../shared/crud/simple-crud";
import schema from "./schema.json";
import * as TableRenderers from "../shared/renderers/table";
import { useConfig } from "../../lib/config";
import { useHttpClient } from "../../lib/http";
import { defineMessages, defineMessage } from "react-intl";
import { FormContainer } from "./form-style";
import { withAuthorizationVerifier } from "../../components/authorization-verifier";
import { SECTION } from "../../lib/constants";

const legalInfoMessages = defineMessages({
  entityName: {
    id: "legalinfo.entityName",
    defaultMessage: "Legal Info"
  },
  policyTypeHelp: {
    id: "legalinfo.policyTypeHelp",
    defaultMessage: "Privacy Policy: Text which indicates compliance with LOPD and GDPR\nCommunication Policy: Text the user will read in order to accept receiving offers and promotions through email"
  },
  translations: {
    id: "legalinfo.translations",
    defaultMessage: "Translations"
  },
  text: {
    id: "legalinfo.text",
    defaultMessage: "Text"
  }
});

const messages: CrudMessages = {
  ...defaultCrudMessages,
  ...legalInfoMessages,
  tableHeaders: {
    hotelTicker: defineMessage({ id: "legalinfo.hotelTicker", defaultMessage: "Hotel Ticker"}),
    property: defineMessage({ id: "legalinfo.property", defaultMessage: "Policy Type"})
  },
};

function LegalInfoCrud({ propertyTicker }: { propertyTicker: string }) {
  const config = useConfig();
  const httpClient = useHttpClient(config.apiEndpoint);

  const onGetAll = useCallback(async () => {
    return await httpClient.get<LegalInfo[]>(`/api/legal/${propertyTicker}/legalInfo`);
  }, [propertyTicker, httpClient]);

  const onGetOne = useCallback(async id => {
    return await httpClient.get<LegalInfo>(`/api/legal/${propertyTicker}/legalInfo/${id}`);
  }, [propertyTicker, httpClient]);

  const onCreate: (entity: LegalInfo) => Promise<number> = useCallback(async entity => {
    const toSend: Partial<LegalInfo> = { ...entity };
    if (toSend.id === -1) {
      toSend.id = undefined;
    }
    const response = await httpClient.post<Partial<LegalInfo>, LegalInfo>(
      `/api/legal/legalInfo`,
      toSend,
    );
    return response.id;
  }, [httpClient]);

  const onUpdate: (entity: LegalInfo) => Promise<void> = useCallback(async entity => {
    await httpClient.post<LegalInfo, any>("/api/legal/legalInfo", entity);
  }, [httpClient]);

  const onDelete = useCallback(async (id: number) => {
    await httpClient.delete<any>(`/api/legal/${propertyTicker}/legalInfo/${id}`);
  }, [httpClient, propertyTicker]);

  const allowedLocales = usePropertyLocales();
  const formConfiguration: FormConfiguration<LegalInfo> = useMemo(() => ({
    id: {
      hidden: true,
      default: -1,
    },
    property: {
      messages: {
        label: messages.tableHeaders?.["property"],
        helpText: legalInfoMessages.policyTypeHelp
      }
    },
    hotelTicker: {
      hidden: true,
      default: propertyTicker,
    },
    translations: {
      ...translationFieldConfig(allowedLocales || []),
      messages: {
        label: legalInfoMessages.translations
      },
      children: {
        id: {
          hidden: true,
        },
        text: {
          messages: {
            label: legalInfoMessages.text,
          },
          widget: "textarea",
        },
        ...translationFieldConfig(allowedLocales || []).children,
      },
    },
  }), [allowedLocales, propertyTicker]);

  return (
    <SimpleCrud<LegalInfo>
      formConfiguration={formConfiguration}
      schema={schema}
      tableColumns={{
        hotelTicker: {
          renderer: TableRenderers.StringRenderer,
          width: 220,
        },
        property: {
          renderer: TableRenderers.StringRenderer,
        },
      }}
      formContainer={FormContainer}
      messages={messages}
      onGetAll={onGetAll}
      onGetOne={onGetOne}
      onCreate={onCreate}
      onUpdate={onUpdate}
      onDelete={onDelete}
      section={SECTION.LEGAL_INFO}
    />
  );
}

export default withAuthorizationVerifier(LegalInfoCrud);
